import { Text, TextBadge, VSpacer } from '@/components/DesignSystem';
import { DashboardModal } from '@/pages/CustomerDashboard/DashboardModal';
import { getHasTierGraphics } from '@/pages/CustomerDashboard/helpers';
import { TierImage } from '@/pages/CustomerDashboard/TierImage';
import { ApiEnergyDashboardTier } from '@api/interfaces/ApiEnergyDashboard';
import { Stack } from '@mui/material';
import { useTranslation } from 'react-i18next';

interface EnergyTierBenefitsModalProps {
  brandColor: string,
  currentRank: number | null,
  onClose: () => void,
  tiers: ApiEnergyDashboardTier[],
}

export const EnergyTierCard = ({
  badge,
  brandColor,
  hasTierGraphics,
  tier,
}: {
  badge?: string,
  brandColor: string,
  hasTierGraphics: boolean,
  tier: ApiEnergyDashboardTier,
}) => {
  const { t } = useTranslation();
  return (
    <Stack
      bgcolor="white"
      borderRadius="8px"
      data-testid={`energy-tier-card-${tier.rank}`}
      p="16px"
    >
      <Stack alignItems="center" direction="row" gap="8px">
        {hasTierGraphics && <TierImage hasTierGraphics tier={tier} />}
        <Text category="title-medium">{tier.name}</Text>
        {!!badge && <TextBadge backgroundColor={brandColor} text={badge} />}
      </Stack>
      <VSpacer size="2" />
      <Text category="body-medium">
        {t(($) => $.customerDashboard.energy.tierRate, {
          effective: tier.effectivePointsPerGallon,
          multiplier: tier.pointsMultiplier,
        })}
      </Text>
      {tier.behaviorsRequired > 0 && (
        <Text category="body-small">
          {t(($) => $.customerDashboard.energy.behaviorsRequired, {
            count: tier.behaviorsRequired,
          })}
        </Text>
      )}
      {!!tier.requiredBehaviorNames.length && (
        <Text category="body-small">
          {t(($) => $.customerDashboard.energy.required, {
            names: tier.requiredBehaviorNames.join(', '),
          })}
        </Text>
      )}
      {tier.benefits.map((benefit) => (
        <Text category="body-small" key={benefit.name}>
          {'• '}{benefit.name}{benefit.description ? ` — ${benefit.description}` : ''}
        </Text>
      ))}
    </Stack>
  );
};

export const EnergyTierBenefitsModal = ({
  brandColor,
  currentRank,
  onClose,
  tiers,
}: EnergyTierBenefitsModalProps) => {
  const { t } = useTranslation();
  const hasTierGraphics = getHasTierGraphics(tiers.map(({ name }) => name));
  const currentTier = tiers.find(({ rank }) => rank === currentRank);

  return (
    <DashboardModal
      onClose={onClose}
      open
      testID="energy-tier-benefits-modal"
      title={t(($) => $.customerDashboard.tierBenefits.title, {
        tierName: currentTier?.name ?? '',
      })}
    >
      <Stack bgcolor="#f6f6f6" gap="16px" p="20px 16px">
        {tiers.map((tier) => (
          <EnergyTierCard
            badge={tier.rank === currentRank
              ? t(($) => $.customerDashboard.energy.current)
              : undefined}
            brandColor={brandColor}
            hasTierGraphics={hasTierGraphics}
            key={tier.rank}
            tier={tier}
          />
        ))}
      </Stack>
    </DashboardModal>
  );
};
